import type Popper from '@popperjs/core';
import { easings, useTransition } from '@react-spring/web';
import { Children } from 'react';
import { useIsPopout } from '../../hooks/useCurrentDocument';
import { Portal } from '../Portal';
import { PopoverContent } from './PopoverContent';
import { PopoverTarget } from './PopoverTarget';
import { POPOVER_PORTAL_ID } from './tokens';
import type { PopoverProps } from './types';

export const getTransition = (placement: Popper.Placement | undefined): Parameters<typeof useTransition>[1] => {
  const direction =
    placement === undefined || placement?.startsWith('top') || placement?.startsWith('bottom') ? 'Y' : 'X';
  const sign = placement === undefined || placement?.startsWith('top') || placement?.startsWith('left') ? '' : '-';
  return {
    overflow: 'hidden',
    from: { opacity: 0, transform: `translate${direction}(${sign}8px)` },
    enter: { opacity: 1, transform: `translate${direction}(0)` },
    leave: { opacity: 0, transform: `translate${direction}(${sign}8px)` },
    config: {
      duration: 300,
      easing: easings.easeOutBack,
    },
  };
};

export function Popover({
  children,
  isOpen,
  onMouseEnterTarget,
  onMouseEnterContent,
  onMouseLeave,
  onFocusTarget,
  onBlurTarget,
  onClickTarget,
  setContentRef,
  setTargetRef,
  isSmall,
  usePortal,
  portalElement,
  tabIndex = 0,
  styles,
  attributes,
  state,
  spacing,
  overflow,
  overflowY = 'auto',
  preventOverflow,
  targetStyle,
  contentStyle,
  variant,
  noPaddingAndBorder,
  zIndex,
  placement,
  maxHeight,
}: PopoverProps) {
  const [targetChild, contentChild] = Children.toArray(children);
  const isPopout = useIsPopout();

  const transitions = useTransition<boolean, Parameters<typeof useTransition>[1]>(isOpen, getTransition(placement));

  if (contentChild == null) {
    return <>{targetChild}</>;
  }

  const maybeMaxWidth = preventOverflow ? `calc(100% - ${spacing * 2}px)` : '';

  const sharedPopoverProps = {
    variant,
    style: { ...styles.popper, maxWidth: maybeMaxWidth }, // safeguard from overflowing by applying maxwidth
    ref: setContentRef,
    placement: (state && state.placement) ?? 'auto',
    onMouseLeave,
    onMouseEnter: onMouseEnterContent,
    isSmall,
    overflow,
    overflowY,
    noPaddingAndBorder,
    'data-testid': 'popover',
    zIndex,
    maxHeight,
    contentStyle,
    ...attributes.popper,
  };

  const popperContent = isPopout
    ? // No transitions for popout mode
      isOpen && (
        <PopoverContent {...sharedPopoverProps} aria-busy={false}>
          {contentChild}
        </PopoverContent>
      )
    : // Use transitions for normal mode
      transitions(
        (transitionStyle, item, t) =>
          item && (
            <PopoverContent
              {...sharedPopoverProps}
              key={t.key}
              transitionStyle={transitionStyle}
              aria-busy={!t.ctrl.idle}
            >
              {contentChild}
            </PopoverContent>
          )
      );

  return (
    <>
      <PopoverTarget
        onMouseEnter={onMouseEnterTarget}
        onMouseLeave={onMouseLeave}
        onFocus={onFocusTarget}
        onBlur={onBlurTarget}
        onClick={onClickTarget}
        ref={setTargetRef}
        tabIndex={tabIndex}
        style={targetStyle}
        data-testid="popover-target"
      >
        {targetChild}
      </PopoverTarget>
      {usePortal || portalElement ? (
        <>
          <Portal portalId={POPOVER_PORTAL_ID} portalElement={portalElement} createTopLevelPortal={true}>
            {popperContent}
          </Portal>
        </>
      ) : (
        popperContent
      )}
    </>
  );
}
